import React from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import PlanDetailView from './PlanDetailView';
import PlanPriceDisplay from './PlanPriceDisplay';
import BillingCycleComparison from './BillingCycleComparison';
import { sellingPriceForCycle } from '../../utils/planPricing';

/**
 * @param {object} props
 * @param {object[]} props.plans
 * @param {string|number|null} props.selectedPlanId
 * @param {(planId: string|number|null) => void} props.onSelectPlan
 * @param {string} props.cycle
 * @param {(cycle: string) => void} props.onCycleChange
 * @param {(plan: object|null, amount: number) => void} [props.onPlanAmountChange]
 * @param {boolean} [props.excludeFree]
 * @param {boolean} [props.showDetail]
 */
export default function PlanPicker({
  plans = [],
  selectedPlanId,
  onSelectPlan,
  cycle,
  onCycleChange,
  onPlanAmountChange,
  excludeFree = true,
  showDetail = true,
}) {
  const visible = excludeFree ? plans.filter((p) => p.slug !== 'free') : plans;
  const selected = visible.find((p) => String(p.id) === String(selectedPlanId)) || null;

  const handleSelect = (plan) => {
    const id = plan?.id ?? null;
    onSelectPlan(id);
    onCycleChange('monthly');
    if (onPlanAmountChange) {
      onPlanAmountChange(plan, plan ? sellingPriceForCycle(plan, 'monthly') : 0);
    }
  };

  const handleCycleSelect = (newCycle) => {
    onCycleChange(newCycle);
    if (selected && onPlanAmountChange) {
      onPlanAmountChange(selected, sellingPriceForCycle(selected, newCycle));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium mb-2">Select subscription plan</p>
        <div className="grid gap-3 sm:grid-cols-2 max-h-[280px] overflow-y-auto pr-1">
          {visible.map((plan) => {
            const isSelected = String(plan.id) === String(selectedPlanId);
            return (
              <Card
                key={plan.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-border'
                } ${plan.is_popular ? 'border-blue-400/50' : ''}`}
                onClick={() => handleSelect(plan)}
              >
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{plan.name}</CardTitle>
                    {plan.is_popular && <Badge className="bg-blue-600 text-white text-xs shrink-0">Popular</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0 space-y-2">
                  <PlanPriceDisplay plan={plan} cycle="monthly" size="sm" showCycleSuffix />
                  <p className="text-xs text-muted-foreground line-clamp-2">{plan.description}</p>
                  <p className="text-xs text-muted-foreground">
                    Up to {plan.features?.limits?.max_students ?? '—'} students
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {visible.length === 0 && (
          <p className="text-sm text-muted-foreground">No active plans available.</p>
        )}
      </div>

      {selected && (
        <div className="rounded-lg border border-border bg-muted/20 p-4">
          <BillingCycleComparison
            plan={selected}
            action="subscribe"
            onSelectCycle={handleCycleSelect}
            selectedCycle={cycle}
            showHeader
          />
        </div>
      )}

      {showDetail && selected && (
        <PlanDetailView plan={selected} compact />
      )}
    </div>
  );
}
